import clsx from 'clsx';
import { Camera, Download, FileArchive, ImagePlus, Lock, LockOpen, Pencil, Plus, RefreshCw, Trash2, Upload, X } from 'lucide-react';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ABSENCE_KINDS, ABSENCE_RULES, APPROVAL_MODES, BILLING_TYPES, HOLIDAY_CALENDARS, SYNC_INTERVALS, type AppSettings, type ApprovalMode, type BillingType } from '@pz/core';
import type { CategoryView } from '@pz/server';
import { useBranding } from '../components/brand';
import { TaskTemplatesTab } from '../components/task-templates';
import { ScrollTable, Sentinel } from '../components/virtual';
import {
  Button,
  Checkbox,
  ConfirmModal,
  EmptyState,
  ErrorBox,
  Field,
  Input,
  Loading,
  Modal,
  Notice,
  PageHeader,
  Panel,
  Pill,
  Select,
  Tabs,
  td,
  th,
  useToast,
} from '../components/ui';
import { categoryName } from '../lib/categories';
import { saveFile } from '../lib/download';
import { logoDataUrl } from '../lib/image';
import { d, dt, h, month, num } from '../lib/format';
import { JobReportsTab } from '../components/job-reports';
import { useM, useQ, useTabParam } from '../lib/hooks';
import { useMe } from '../lib/me';
import { ApprovalCompare } from '../components/approval-compare';
import { rpc } from '../lib/rpc';

type Tab = 'settings' | 'categories' | 'templates' | 'periods' | 'forma' | 'backup' | 'audit' | 'outlook' | 'jobs';

export function AdminPage() {
  const { t } = useTranslation();
  const { can } = useMe();
  // from the most used to the least used (round 5, request 35): the month closing every month, the Forma
  // synchronisation and the reports of the nightly maintenance often, the settings and the Outlook connection now and
  // then, the log when something is looked into, backup, categories and task templates rarely. The first visible tab
  // opens: the office starts with the month closing, the technical administration with Forma.
  const tabs: { id: Tab; label: string; hidden?: boolean }[] = [
    { id: 'periods', label: t('admin.tabs.periods'), hidden: !can('period.manage', 'time.manage', 'billing.manage') },
    { id: 'forma', label: t('admin.tabs.forma'), hidden: !can('sync.manage', 'projects.manage') },
    // reports of the nightly maintenance and the daily check (request 32 of round 4)
    { id: 'jobs', label: t('admin.tabs.jobs'), hidden: !can('settings.manage', 'sync.manage') },
    { id: 'settings', label: t('admin.tabs.settings'), hidden: !can('settings.manage') },
    { id: 'outlook', label: t('admin.tabs.outlook'), hidden: !can('settings.manage', 'hr.manage') },
    { id: 'audit', label: t('admin.tabs.audit'), hidden: !can('audit.view') },
    { id: 'backup', label: t('admin.tabs.backup'), hidden: !can('backup.manage', 'data.export.all') },
    { id: 'categories', label: t('admin.tabs.categories'), hidden: !can('settings.manage') },
    { id: 'templates', label: t('admin.tabs.templates'), hidden: !can('projects.manage') },
  ];
  const visible = tabs.filter((x) => !x.hidden).map((x) => x.id);
  const [tab, setTab] = useTabParam<Tab>(visible.length ? visible : ['periods']);
  return (
    <div>
      <PageHeader title={t('admin.title')} />
      <Tabs value={tab} onChange={setTab} tabs={tabs} />
      {tab === 'settings' && <SettingsTab />}
      {tab === 'categories' && <CategoriesTab />}
      {tab === 'templates' && <TaskTemplatesTab />}
      {tab === 'periods' && <PeriodsTab />}
      {tab === 'forma' && <FormaAdminTab />}
      {tab === 'outlook' && <OutlookTab />}
      {tab === 'backup' && <BackupTab />}
      {tab === 'audit' && <AuditTab />}
      {tab === 'jobs' && <JobReportsTab />}
    </div>
  );
}

/**
 * Announcements by e-mail (round 5, request 31): switched on here, and whose address sends them: the person who
 * publishes, or one fixed person (assistant of the management, admin or managing director).
 */
function AnnouncementMailSettings({ value, onChange }: { value: NonNullable<AppSettings['announcements']>; onChange: (v: Partial<NonNullable<AppSettings['announcements']>>) => void }) {
  const { t } = useTranslation();
  const q = useQ('announcements.mailSenders', {});
  const persons = q.data?.persons ?? [];
  const chosen = value.sender === 'person' ? persons.find((p) => p.id === value.senderPersonId) : null;
  return (
    <Panel title={t('admin.settings.annMail')}>
      <div className="flex flex-col gap-3">
        <Checkbox label={t('admin.settings.annMailEnabled')} hint={t('admin.settings.annMailEnabledHint')} checked={value.email} onChange={(e) => onChange({ email: e.target.checked })} />
        <Field label={t('admin.settings.annMailSender')} hint={t('admin.settings.annMailSenderHint')}>
          <Select
            value={value.sender === 'person' && value.senderPersonId ? value.senderPersonId : ''}
            disabled={!value.email}
            onChange={(e) => onChange(e.target.value ? { sender: 'person', senderPersonId: e.target.value } : { sender: 'publisher', senderPersonId: null })}
          >
            <option value="">{t('admin.settings.annMailPublisher')}</option>
            {persons.map((p) => (
              <option key={p.id} value={p.id}>
                {`${p.name} (${p.email || '–'}) · ${t(`people.roles.${p.role as 'office'}`, { defaultValue: p.role })}`}
              </option>
            ))}
          </Select>
        </Field>
        {chosen && !chosen.valid && <Notice tone="warn">{t('admin.settings.annMailNoAddress')}</Notice>}
        {q.data && !q.data.configured && <Notice tone="warn">{t('admin.settings.annMailNotConfigured')}</Notice>}
        <p className="text-xs text-graphite">{t('admin.settings.annMailHint')}</p>
      </div>
    </Panel>
  );
}

/** The settings of the page without the sections of Abrechnung and Kunden (changed in the modules, request 38). */
function withoutModules(s: AppSettings): Partial<AppSettings> {
  const { billing: _billing, customers: _customers, ...rest } = s;
  return rest;
}

function SettingsTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const q = useQ('settings.get', {});
  const [s, setS] = useState<AppSettings | null>(null);
  useEffect(() => {
    if (q.data) setS(q.data.settings);
  }, [q.data]);
  const save = useM('settings.update', { invalidate: ['settings.', 'me.'] });
  if (q.isLoading || !s) return <Loading />;
  const upd = <K extends keyof AppSettings>(k: K, v: Partial<AppSettings[K]>) => setS({ ...s, [k]: { ...s[k], ...v } });
  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button
          variant="primary"
          loading={save.isPending}
          onClick={() => {
            // the settings as they were before: "Rückgängig" in the message writes them back
            const before = q.data?.settings ? withoutModules(q.data.settings) : undefined;
            save
              .mutateAsync({ patch: withoutModules(s) as never })
              .then(() =>
                before
                  ? toast.undoable(t('common.saved'), t('common.undo'), () =>
                      save
                        .mutateAsync({ patch: before as never })
                        .then(() => toast.ok(t('common.undone')))
                        .catch(toast.error),
                    )
                  : toast.ok(t('common.saved')),
              )
              .catch(toast.error);
          }}
        >
          {t('common.save')}
        </Button>
      </div>
      {/* panels keep their own height: a short panel does not stretch next to a long one */}
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Panel title={t('admin.settings.company')}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('admin.settings.companyName')} hint={t('admin.settings.companyNameHint')} className="sm:col-span-2">
              <Input value={s.company.name} maxLength={120} onChange={(e) => upd('company', { name: e.target.value })} />
            </Field>
            <LogoField />
            <Field label={t('admin.settings.appName')}>
              <Input value={s.company.appName} onChange={(e) => upd('company', { appName: e.target.value })} />
            </Field>
            <Field label={t('admin.settings.place')}>
              <Input value={s.company.place} onChange={(e) => upd('company', { place: e.target.value })} />
            </Field>
            {/* quick links of the side bar: a link appears only with an address; an empty label keeps the standard label */}
            <div className="flex flex-col gap-2 sm:col-span-2">
              <span className="text-sm font-medium">{t('admin.settings.links')}</span>
              <span className="text-xs text-graphite">{t('admin.settings.linksHint')}</span>
              {(
                [
                  ['standards', 'standardsUrl'],
                  ['meetings', 'meetingNotesUrl'],
                  ['website', 'websiteUrl'],
                  ['formaHome', 'formaHomeUrl'],
                  ['sharepoint', 'sharepointUrl'],
                ] as const
              ).map(([key, field]) => (
                <div key={key} className="grid gap-2 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                  <Field label={t(`admin.settings.linkUrl.${key}`)}>
                    <Input type="url" inputMode="url" placeholder="https://" value={s.company[field] ?? ''} maxLength={500} onChange={(e) => upd('company', { [field]: e.target.value })} />
                  </Field>
                  <Field label={t('admin.settings.linkLabel')}>
                    <Input
                      value={s.company.linkLabels?.[key] ?? ''}
                      maxLength={40}
                      placeholder={t(`shell.links.${key}`)}
                      onChange={(e) => upd('company', { linkLabels: { ...(s.company.linkLabels ?? { standards: '', meetings: '', website: '', formaHome: '', sharepoint: '' }), [key]: e.target.value } })}
                    />
                  </Field>
                </div>
              ))}
            </div>
            <Field label={t('admin.settings.defaultCalendar')}>
              <Select value={s.company.defaultHolidayCalendar} onChange={(e) => upd('company', { defaultHolidayCalendar: e.target.value })}>
                {HOLIDAY_CALENDARS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </Panel>
        <Panel title={t('admin.settings.approval')}>
          <div className="flex flex-col gap-3">
            {/* the company default of the approval; each project can choose its own (project > overview > approval) */}
            <Field label={t('admin.settings.approvalMode')} hint={t('admin.settings.approvalModeHint')}>
              <Select
                value={s.approval.mode ?? (s.approval.stages === 1 ? 'lead' : 'lead_office')}
                onChange={(e) => {
                  const mode = e.target.value as ApprovalMode;
                  upd('approval', { mode, stages: mode === 'lead_office' ? 2 : 1 });
                }}
              >
                {APPROVAL_MODES.map((m) => (
                  <option key={m} value={m}>
                    {t(`projects.approval.modes.${m}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Checkbox label={t('admin.settings.selfApproval')} hint={t('admin.settings.selfApprovalHint')} checked={s.approval.allowSelfApproval} onChange={(e) => upd('approval', { allowSelfApproval: e.target.checked })} />
            <ApprovalCompare />
          </div>
        </Panel>
        <Panel title={t('admin.settings.time')}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('admin.settings.step')}>
              <Select value={s.time.stepMinutes} onChange={(e) => upd('time', { stepMinutes: Number(e.target.value) })}>
                {[1, 5, 6, 10, 15, 30, 60].map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('admin.settings.warnDaily')}>
              <Input type="number" value={s.time.warnDailyMinutes / 60} onChange={(e) => upd('time', { warnDailyMinutes: Math.round(Number(e.target.value) * 60) })} />
            </Field>
            <Field label={t('admin.settings.maxFuture')}>
              <Input type="number" value={s.time.maxFutureDays} onChange={(e) => upd('time', { maxFutureDays: Number(e.target.value) })} />
            </Field>
            <Field label={t('admin.settings.deadline')}>
              <Input type="number" value={s.time.submitDeadlineDays} onChange={(e) => upd('time', { submitDeadlineDays: Number(e.target.value) })} />
            </Field>
            <Field label={t('admin.settings.trackingStart')} hint={t('admin.settings.trackingStartHint')} className="sm:col-span-2">
              <Input type="date" className="w-48" value={s.time.trackingStart ?? ''} onChange={(e) => upd('time', { trackingStart: e.target.value || null })} />
            </Field>
            <Field label={t('admin.settings.workLocations')} hint={t('admin.settings.workLocationsHint')} className="sm:col-span-2">
              <ListEditor values={s.time.workLocations} onChange={(v) => upd('time', { workLocations: v })} />
            </Field>
            <Field label={t('admin.settings.requireDescription')} className="sm:col-span-2">
              <div className="flex flex-wrap gap-4">
                {BILLING_TYPES.map((b: BillingType) => (
                  <Checkbox
                    key={b}
                    label={t(`billingType.${b}`)}
                    checked={s.time.requireDescriptionFor.includes(b)}
                    onChange={(e) =>
                      upd('time', {
                        requireDescriptionFor: e.target.checked ? [...s.time.requireDescriptionFor, b] : s.time.requireDescriptionFor.filter((x) => x !== b),
                      })
                    }
                  />
                ))}
              </div>
            </Field>
            <Checkbox className="sm:col-span-2" label={t('admin.settings.ownBillingType')} checked={s.time.ownBillingType !== false} onChange={(e) => upd('time', { ownBillingType: e.target.checked })} />
            <Checkbox
              className="sm:col-span-2"
              label={t('admin.settings.noOvertime')}
              hint={t('admin.settings.noOvertimeHint')}
              checked={s.time.noOvertime === true}
              onChange={(e) => upd('time', { noOvertime: e.target.checked })}
            />
          </div>
        </Panel>
        {/* the defaults of the documents and the payment term belong to Abrechnung and Kunden (round 5, request 38) */}
        <Panel title={t('admin.settings.billing')}>
          <p className="text-sm text-graphite">{t('admin.settings.billingMoved')}</p>
        </Panel>
        <Panel title={t('admin.settings.hrKpi')}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('admin.settings.tolerance')} hint={t('admin.settings.toleranceHint')} className="sm:col-span-2">
              <Input
                type="number"
                min={0}
                max={20}
                step={0.5}
                className="w-32"
                value={s.kpi.weekToleranceMinutes / 60}
                onChange={(e) => upd('kpi', { weekToleranceMinutes: Math.round(Number(e.target.value) * 60) })}
              />
            </Field>
            <Checkbox className="sm:col-span-2" label={t('admin.settings.teamKpi')} checked={s.kpi.teamEnabled} onChange={(e) => upd('kpi', { teamEnabled: e.target.checked })} />
            <Checkbox className="sm:col-span-2" label={t('admin.settings.leadLoadLevels')} checked={s.kpi.leadLoadLevels !== false} onChange={(e) => upd('kpi', { leadLoadLevels: e.target.checked })} />
          </div>
        </Panel>
        <Panel title={t('admin.settings.security')}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Checkbox label={t('admin.settings.totpExternal')} checked={s.security.requireTotpForExternal} onChange={(e) => upd('security', { requireTotpForExternal: e.target.checked })} />
            <Checkbox label={t('admin.settings.totpPrivileged')} checked={s.security.requireTotpForPrivileged} onChange={(e) => upd('security', { requireTotpForPrivileged: e.target.checked })} />
            <Field label={t('admin.settings.idle')}>
              <Input type="number" value={s.security.sessionIdleHours} onChange={(e) => upd('security', { sessionIdleHours: Number(e.target.value) })} />
            </Field>
            <Field label={t('admin.settings.maxDays')}>
              <Input type="number" value={s.security.sessionMaxDays} onChange={(e) => upd('security', { sessionMaxDays: Number(e.target.value) })} />
            </Field>
          </div>
        </Panel>
        <Panel title={`${t('admin.settings.forma')} / ${t('admin.settings.bi')}`}>
          <div className="flex flex-col gap-3">
            <Field label={t('admin.settings.deepLink')}>
              <Input value={s.forma.deepLinkTemplate} onChange={(e) => upd('forma', { deepLinkTemplate: e.target.value })} />
            </Field>
            <Field label={t('admin.forma.filesTemplate')}>
              <Input value={s.forma.filesLinkTemplate ?? ''} maxLength={300} onChange={(e) => upd('forma', { filesLinkTemplate: e.target.value })} />
            </Field>
            <Field label={t('admin.forma.boardTemplate')} hint={t('admin.forma.linksHint')}>
              <Input value={s.forma.boardLinkTemplate ?? ''} maxLength={300} onChange={(e) => upd('forma', { boardLinkTemplate: e.target.value })} />
            </Field>
            <div className="flex flex-wrap gap-3">
              <Field label={t('admin.settings.syncInterval')}>
                <Select value={s.forma.syncIntervalMinutes} onChange={(e) => upd('forma', { syncIntervalMinutes: Number(e.target.value) })} className="w-36">
                  {SYNC_INTERVALS.map((n) => (
                    <option key={n} value={n}>
                      {t('admin.settings.minutes', { count: n })}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('admin.settings.syncWindow')}>
                <Select value={s.forma.syncWindow} onChange={(e) => upd('forma', { syncWindow: e.target.value as 'daily' })} className="w-52">
                  <option value="daily">{t('admin.settings.windows.daily')}</option>
                  <option value="workhours">{t('admin.settings.windows.workhours')}</option>
                  <option value="always">{t('admin.settings.windows.always')}</option>
                </Select>
              </Field>
            </div>
            <Checkbox label={t('admin.settings.autoImport')} checked={s.forma.autoImportProjects} onChange={(e) => upd('forma', { autoImportProjects: e.target.checked })} />
            <Checkbox label={t('admin.settings.pseudonymize')} checked={s.bi.pseudonymize} onChange={(e) => upd('bi', { pseudonymize: e.target.checked })} />
          </div>
        </Panel>
        <Panel title={t('admin.settings.leave')}>
          <div className="flex flex-col gap-1.5">
            <div className="text-[13px] font-medium text-graphite">{t('admin.settings.teamVisible')}</div>
            {ABSENCE_KINDS.filter((k) => !ABSENCE_RULES[k].health).map((k) => (
              <Checkbox
                key={k}
                label={t(`week.absence.${k}`)}
                checked={s.leave.teamVisibleKinds.includes(k)}
                onChange={(e) => upd('leave', { teamVisibleKinds: e.target.checked ? [...s.leave.teamVisibleKinds, k] : s.leave.teamVisibleKinds.filter((x) => x !== k) })}
              />
            ))}
            <p className="mt-1 text-xs text-graphite">{t('admin.settings.teamVisibleHint')}</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label={t('admin.settings.docSick')}>
                <Input
                  type="number"
                  min={0}
                  max={42}
                  className="w-28"
                  value={s.leave.documentFromDay.sick}
                  onChange={(e) => upd('leave', { documentFromDay: { ...s.leave.documentFromDay, sick: Math.max(0, Math.min(42, Math.round(Number(e.target.value) || 0))) } })}
                />
              </Field>
              <Field label={t('admin.settings.docChildSick')}>
                <Input
                  type="number"
                  min={0}
                  max={42}
                  className="w-28"
                  value={s.leave.documentFromDay.child_sick}
                  onChange={(e) => upd('leave', { documentFromDay: { ...s.leave.documentFromDay, child_sick: Math.max(0, Math.min(42, Math.round(Number(e.target.value) || 0))) } })}
                />
              </Field>
              <p className="text-xs text-graphite sm:col-span-2">{t('admin.settings.docHint')}</p>
              <Field label={t('admin.settings.retention')} hint={t('admin.settings.retentionHint')} className="sm:col-span-2">
                <Input
                  type="number"
                  min={0}
                  max={240}
                  className="w-28"
                  value={s.leave.attachmentRetentionMonths}
                  onChange={(e) => upd('leave', { attachmentRetentionMonths: Math.max(0, Math.min(240, Math.round(Number(e.target.value) || 0))) })}
                />
              </Field>
            </div>
            {/* leave days in Turkey (request 24 of round 4): Saturday of a 5-day week as a leave day */}
            <div className="mt-3 border-t border-line pt-3">
              <Checkbox
                label={t('admin.settings.saturdayLeaveTR')}
                hint={t('admin.settings.saturdayLeaveTRHint')}
                checked={s.leave.saturdayLeaveTR}
                onChange={(e) => upd('leave', { saturdayLeaveTR: e.target.checked })}
              />
              {/* A-15 (round 5): warning for the Turkish split rule, off by default */}
              <Checkbox className="mt-2" label={t('admin.settings.splitRuleTR')} hint={t('admin.settings.splitRuleTRHint')} checked={!!s.leave.splitRuleTR} onChange={(e) => upd('leave', { splitRuleTR: e.target.checked })} />
              {/* request 27 (round 5): two stages for people with an assigned approver, off by default */}
              <Checkbox className="mt-2" label={t('admin.settings.leaveTwoStage')} hint={t('admin.settings.leaveTwoStageHint')} checked={!!s.leave.twoStage} onChange={(e) => upd('leave', { twoStage: e.target.checked })} />
              <p className="mt-2 text-xs text-graphite">{t('admin.settings.leaveRulesHint')}</p>
            </div>
          </div>
        </Panel>
        <Panel title={t('admin.settings.outlook')}>
          <div className="flex flex-col gap-3">
            <Checkbox label={t('admin.settings.outlookEnabled')} checked={s.outlook.enabled} onChange={(e) => upd('outlook', { enabled: e.target.checked })} />
            <Checkbox label={t('admin.settings.outlookPersonal')} checked={s.outlook.personalCalendars} disabled={!s.outlook.enabled} onChange={(e) => upd('outlook', { personalCalendars: e.target.checked })} />
            {/* holidays of each person as appointments (round 5, request 25) */}
            <Checkbox
              label={t('admin.settings.outlookHolidays')}
              hint={t('admin.settings.outlookHolidaysHint')}
              checked={s.outlook.holidays !== false}
              disabled={!s.outlook.enabled || !s.outlook.personalCalendars}
              onChange={(e) => upd('outlook', { holidays: e.target.checked })}
            />
            <Field label={t('admin.settings.outlookTeam')}>
              <Input type="email" value={s.outlook.teamMailbox} disabled={!s.outlook.enabled} placeholder="abwesenheiten@firma.de" onChange={(e) => upd('outlook', { teamMailbox: e.target.value })} />
            </Field>
            <p className="text-xs text-graphite">{t('admin.settings.outlookHint')}</p>
          </div>
        </Panel>
        <AnnouncementMailSettings value={s.announcements ?? { email: false, sender: 'publisher', senderPersonId: null }} onChange={(v) => upd('announcements', v)} />
        <Panel title={t('admin.settings.automations')}>
          <div className="flex flex-col gap-3">
            <p className="text-xs text-graphite">{t('admin.settings.automationsIntro')}</p>
            <Checkbox label={t('admin.settings.autoWeekly')} checked={s.automations.weeklyReminder} onChange={(e) => upd('automations', { weeklyReminder: e.target.checked })} />
            <Checkbox label={t('admin.settings.autoMissing')} checked={s.automations.missingEntries} onChange={(e) => upd('automations', { missingEntries: e.target.checked })} />
            <Field label={t('admin.settings.autoMissingDays')}>
              <Input
                type="number"
                min={1}
                max={31}
                className="w-28"
                disabled={!s.automations.missingEntries}
                value={s.automations.missingEntriesLookbackDays}
                onChange={(e) => upd('automations', { missingEntriesLookbackDays: Math.max(1, Math.min(31, Math.round(Number(e.target.value) || 1))) })}
              />
            </Field>
            <Checkbox label={t('admin.settings.autoPending')} checked={s.automations.pendingApprovals} onChange={(e) => upd('automations', { pendingApprovals: e.target.checked })} />
            <Field label={t('admin.settings.autoPendingDays')}>
              <Input
                type="number"
                min={1}
                max={30}
                className="w-28"
                disabled={!s.automations.pendingApprovals}
                value={s.automations.pendingEscalationDays}
                onChange={(e) => upd('automations', { pendingEscalationDays: Math.max(1, Math.min(30, Math.round(Number(e.target.value) || 1))) })}
              />
            </Field>
            <Checkbox label={t('admin.settings.autoMonth')} checked={s.automations.monthCloseReminder} onChange={(e) => upd('automations', { monthCloseReminder: e.target.checked })} />
          </div>
        </Panel>
        <Panel title={t('admin.settings.jobsTitle')}>
          <dl className="flex flex-col gap-2 text-sm">
            {[
              [t('admin.settings.jobForma'), t('admin.settings.jobFormaWhen', { minutes: s.forma.syncIntervalMinutes, window: t(`admin.settings.windows.${s.forma.syncWindow}`) })],
              [t('admin.settings.jobOutlook'), t('admin.settings.jobOutlookWhen')],
              [t('admin.settings.jobNightly'), t('admin.settings.jobNightlyWhen')],
            ].map(([name, when]) => (
              <div key={name} className="grid gap-x-3 gap-y-0.5 sm:grid-cols-[9rem_minmax(0,1fr)]">
                <dt className="font-medium">{name}</dt>
                <dd className="text-graphite">{when}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-3 text-xs text-graphite">{t('admin.settings.jobsHint')}</p>
        </Panel>
      </div>
      <ErrorBox error={save.error} />
    </div>
  );
}

/** Small list editor (work locations): remove with x, add with the field below. */
function ListEditor({ values, onChange }: { values: string[]; onChange: (v: string[]) => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const add = () => {
    const v = text.trim();
    if (!v || values.includes(v)) return;
    onChange([...values, v]);
    setText('');
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-1.5">
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 rounded-full border border-line bg-sheet-2 py-0.5 pr-1 pl-2.5 text-sm">
            {v}
            <button type="button" aria-label={`${t('common.delete')}: ${v}`} className="rounded-full p-0.5 text-graphite hover:bg-bad-soft hover:text-bad" disabled={values.length <= 1} onClick={() => onChange(values.filter((x) => x !== v))}>
              <X className="size-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          className="w-64"
          value={text}
          maxLength={60}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button size="sm" icon={<Plus className="size-3.5" />} onClick={add} disabled={!text.trim()}>
          {t('common.add')}
        </Button>
      </div>
    </div>
  );
}

/** Company logo: shown in the navigation, on the sign-in page and in the header of exports. Saved immediately. */
function LogoField() {
  const { t } = useTranslation();
  const toast = useToast();
  const brand = useBranding().data;
  const file = useRef<HTMLInputElement>(null);
  const set = useM('branding.setLogo', { invalidate: ['branding.'] });
  return (
    <Field label={t('admin.settings.logo')} hint={t('admin.settings.logoHint')} className="sm:col-span-2">
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex h-14 min-w-28 items-center justify-center rounded-md bg-white px-2 ring-1 ring-line">
          {brand?.logo ? <img src={brand.logo} alt="" className="max-h-12 max-w-56 object-contain" /> : <span className="text-xs text-[#56626f]">{t('admin.settings.noLogo')}</span>}
        </span>
        <Button size="sm" icon={<ImagePlus className="size-3.5" />} loading={set.isPending} onClick={() => file.current?.click()}>
          {brand?.logo ? t('admin.settings.logoChange') : t('admin.settings.logoUpload')}
        </Button>
        {brand?.logo && (
          <Button size="sm" variant="ghost" icon={<Trash2 className="size-3.5" />} onClick={() => set.mutateAsync({ dataUrl: null }).catch(toast.error)}>
            {t('common.delete')}
          </Button>
        )}
        <input
          ref={file}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml,image/webp"
          className="hidden"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (!f) return;
            try {
              await set.mutateAsync({ dataUrl: await logoDataUrl(f) });
              toast.ok(t('common.saved'));
            } catch (err) {
              toast.error(err);
            }
          }}
        />
      </div>
    </Field>
  );
}

interface CategoryForm {
  key?: string;
  nameDe: string;
  nameEn: string;
  nameTr: string;
  group: 'projekt' | 'intern';
  defaultBillingType: BillingType;
  billingTypeFixed: boolean;
  /** hours on tasks of the category do not count as extra hours (setting time.noOvertime) */
  noOvertime: boolean;
  active: boolean;
  sort: number;
}

/** Task categories (Tätigkeitskategorien) for platform tasks; "intern" = overhead, never billed. */
function CategoriesTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const q = useQ('categories.list', {});
  const save = useM('categories.save', { invalidate: ['categories.', 'tasks.'] });
  const createInternal = useM('categories.createInternalTasks', { invalidate: ['tasks.', 'categories.'] });
  const [form, setForm] = useState<CategoryForm | null>(null);
  const open = (c?: CategoryView) =>
    setForm(
      c
        ? { key: c.key, nameDe: c.nameDe, nameEn: c.nameEn ?? '', nameTr: c.nameTr ?? '', group: c.group, defaultBillingType: c.defaultBillingType, billingTypeFixed: c.billingTypeFixed, noOvertime: c.noOvertime, active: c.active, sort: c.sort }
        : { nameDe: '', nameEn: '', nameTr: '', group: 'projekt', defaultBillingType: 'billable', billingTypeFixed: false, noOvertime: false, active: true, sort: 0 },
    );
  const list = q.data?.categories ?? [];
  return (
    <div className="flex flex-col gap-3">
      <Notice tone="neutral">{t('categories.hint')}</Notice>
      <div className="flex flex-wrap gap-2">
        <Button icon={<Plus className="size-4" />} onClick={() => open()}>
          {t('categories.new')}
        </Button>
        <Button
          loading={createInternal.isPending}
          onClick={() =>
            createInternal
              .mutateAsync({})
              .then((r) => toast.ok(r.created.length ? t('categories.internalCreated', { count: r.created.length, project: r.projectNumber }) : t('categories.internalNone')))
              .catch(toast.error)
          }
        >
          {t('categories.createInternal')}
        </Button>
      </div>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {(['projekt', 'intern'] as const).map((g) => (
        <Panel key={g} title={t(`categories.groups.${g}`)} bodyClassName="p-0">
          <div className="scroll-x">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="border-b border-line bg-sheet-2">
                <tr>
                  <th className={th}>{t('common.name')}</th>
                  <th className={th}>{t('categories.translations')}</th>
                  <th className={th}>{t('entry.billingType')}</th>
                  <th className={clsx(th, 'text-right')}>{t('categories.tasks')}</th>
                  <th className={th}>{t('common.status')}</th>
                  <th className={th} />
                </tr>
              </thead>
              <tbody>
                {list
                  .filter((c) => c.group === g)
                  .map((c) => (
                    <tr key={c.key} className={clsx('border-b border-line/70', !c.active && 'text-graphite')}>
                      <td className={td}>
                        <div className="font-medium">{categoryName(c)}</div>
                        <div className="text-xs text-graphite">{c.key}</div>
                      </td>
                      <td className={clsx(td, 'text-xs text-graphite')}>
                        {c.nameEn ?? '-'} · {c.nameTr ?? '-'}
                      </td>
                      <td className={td}>
                        {t(`billingType.${c.defaultBillingType}`)}
                        {c.billingTypeFixed && <Pill className="ml-1">{t('categories.fixed')}</Pill>}
                        {c.noOvertime && <Pill className="ml-1" tone="info">{t('categories.noOvertime')}</Pill>}
                      </td>
                      <td className={clsx(td, 'num text-right')}>{c.taskCount}</td>
                      <td className={td}>{c.active ? <Pill tone="good">{t('common.active')}</Pill> : <Pill>{t('common.inactive')}</Pill>}</td>
                      <td className={clsx(td, 'text-right')}>
                        <Button size="sm" variant="ghost" icon={<Pencil className="size-3.5" />} onClick={() => open(c)}>
                          {t('common.edit')}
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ))}
      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title={form?.key ? t('categories.edit') : t('categories.new')}
        footer={
          <>
            <Button onClick={() => setForm(null)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={!form || form.nameDe.trim().length < 2}
              loading={save.isPending}
              onClick={() =>
                form &&
                save
                  .mutateAsync({ ...form, nameEn: form.nameEn || null, nameTr: form.nameTr || null })
                  .then(() => {
                    toast.ok(t('common.saved'));
                    setForm(null);
                  })
                  .catch(() => {})
              }
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        {form && (
          <div className="flex flex-col gap-3">
            <Field label={t('categories.nameDe')}>
              <Input value={form.nameDe} maxLength={80} onChange={(e) => setForm({ ...form, nameDe: e.target.value })} />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('categories.nameEn')} hint={t('common.optional')}>
                <Input value={form.nameEn} maxLength={80} onChange={(e) => setForm({ ...form, nameEn: e.target.value })} />
              </Field>
              <Field label={t('categories.nameTr')} hint={t('common.optional')}>
                <Input value={form.nameTr} maxLength={80} onChange={(e) => setForm({ ...form, nameTr: e.target.value })} />
              </Field>
            </div>
            <Field label={t('categories.group')}>
              <Select
                value={form.group}
                onChange={(e) => {
                  const group = e.target.value as 'projekt' | 'intern';
                  setForm({ ...form, group, ...(group === 'intern' ? { defaultBillingType: 'non_billable' as BillingType, billingTypeFixed: true } : {}) });
                }}
              >
                <option value="projekt">{t('categories.groups.projekt')}</option>
                <option value="intern">{t('categories.groups.intern')}</option>
              </Select>
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('entry.billingType')}>
                <Select value={form.defaultBillingType} onChange={(e) => setForm({ ...form, defaultBillingType: e.target.value as BillingType })}>
                  {BILLING_TYPES.map((b) => (
                    <option key={b} value={b}>
                      {t(`billingType.${b}`)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('categories.sort')}>
                <Input type="number" min={0} value={form.sort} onChange={(e) => setForm({ ...form, sort: Number(e.target.value) })} />
              </Field>
            </div>
            <Checkbox label={t('categories.fixedLabel')} hint={t('categories.fixedHint')} checked={form.billingTypeFixed} onChange={(e) => setForm({ ...form, billingTypeFixed: e.target.checked })} />
            <Checkbox label={t('categories.activeLabel')} hint={t('categories.activeHint')} checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            <Checkbox label={t('categories.noOvertimeLabel')} hint={t('categories.noOvertimeHint')} checked={form.noOvertime} onChange={(e) => setForm({ ...form, noOvertime: e.target.checked })} />
            {form.key && form.noOvertime !== !!list.find((c) => c.key === form.key)?.noOvertime && <Notice tone="warn">{t('categories.noOvertimeRetro')}</Notice>}
            <ErrorBox error={save.error} />
          </div>
        )}
      </Modal>
    </div>
  );
}

function PeriodsTab() {
  const { t } = useTranslation();
  const { me } = useMe();
  const toast = useToast();
  const [year, setYear] = useState(Number(me.today.slice(0, 4)));
  const q = useQ('periods.list', { year });
  const lock = useM('periods.lock', { invalidate: ['periods.', 'time.'] });
  const unlock = useM('periods.unlock', { invalidate: ['periods.', 'time.'] });
  const [force, setForce] = useState<{ month: string; count: number } | null>(null);
  const [reopen, setReopen] = useState<string | null>(null);
  const doLock = (m: string, f = false) =>
    lock
      .mutateAsync({ month: m, force: f })
      .then((r) => {
        if (!r.locked) setForce({ month: m, count: r.openCount });
        else {
          setForce(null);
          toast.ok(t('admin.periods.locked'));
        }
      })
      .catch(toast.error);
  return (
    <div className="flex flex-col gap-3">
      <Notice tone="neutral">{t('admin.periods.hint')}</Notice>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => setYear(year - 1)}>
          {year - 1}
        </Button>
        <span className="font-semibold">{year}</span>
        <Button size="sm" onClick={() => setYear(year + 1)}>
          {year + 1}
        </Button>
      </div>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && (
        <div className="scroll-x rounded-lg border border-line bg-sheet">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="border-b border-line bg-sheet-2">
              <tr>
                <th className={th}>{t('common.month')}</th>
                <th className={clsx(th, 'text-right')}>{t('admin.periods.openEntries')}</th>
                <th className={clsx(th, 'text-right')}>{t('admin.periods.submitted')}</th>
                <th className={clsx(th, 'text-right')}>{t('admin.periods.approved')}</th>
                <th className={clsx(th, 'text-right')}>{t('admin.periods.billed')}</th>
                <th className={clsx(th, 'text-right')}>{t('common.hours')}</th>
                <th className={th}>{t('common.status')}</th>
                <th className={th} />
              </tr>
            </thead>
            <tbody>
              {q.data.months.map((m) => (
                <tr key={m.month} className="border-b border-line/70">
                  <td className={td}>{month(m.month)}</td>
                  <td className={clsx(td, 'num text-right', m.open > 0 && 'text-warn')}>{m.open}</td>
                  <td className={clsx(td, 'num text-right', m.submitted > 0 && 'text-info')}>{m.submitted}</td>
                  <td className={clsx(td, 'num text-right')}>{m.approved}</td>
                  <td className={clsx(td, 'num text-right')}>{m.billed}</td>
                  <td className={clsx(td, 'num text-right')}>{h(m.minutes, 0)}</td>
                  <td className={td}>
                    {m.locked ? (
                      <Pill tone="neutral" title={`${dt(m.lockedAt)} ${m.lockedBy ?? ''}`}>
                        <Lock className="size-3" /> {t('admin.periods.locked')}
                      </Pill>
                    ) : (
                      <Pill tone="info">{t('admin.periods.open')}</Pill>
                    )}
                  </td>
                  <td className={clsx(td, 'text-right')}>
                    {q.data.canManage &&
                      (m.locked ? (
                        <Button size="sm" variant="ghost" icon={<LockOpen className="size-3.5" />} onClick={() => setReopen(m.month)}>
                          {t('admin.periods.unlock')}
                        </Button>
                      ) : (
                        m.minutes > 0 && (
                          <Button size="sm" icon={<Lock className="size-3.5" />} loading={lock.isPending} onClick={() => doLock(m.month)}>
                            {t('admin.periods.lock')}
                          </Button>
                        )
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmModal
        open={!!force}
        onClose={() => setForce(null)}
        title={t('admin.periods.forceTitle')}
        text={t('admin.periods.forceText', { count: force?.count ?? 0 })}
        confirmLabel={t('admin.periods.lock')}
        danger
        loading={lock.isPending}
        onConfirm={() => force && doLock(force.month, true)}
      />
      <ConfirmModal
        open={!!reopen}
        onClose={() => setReopen(null)}
        title={t('admin.periods.unlockTitle')}
        confirmLabel={t('admin.periods.unlock')}
        reason="required"
        loading={unlock.isPending}
        error={unlock.error}
        onConfirm={(reason) =>
          unlock
            .mutateAsync({ month: reopen!, reason })
            .then(() => setReopen(null))
            .catch(() => {})
        }
      />
    </div>
  );
}

function FormaAdminTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const { can } = useMe();
  // connecting Forma and runs over all projects are technical operation (sync.manage); project staff with
  // projects.manage see the state here and sync single projects on the project page
  const operate = can('sync.manage');
  const q = useQ('forma.status', {});
  const sync = useM('forma.syncNow', { invalidate: ['forma.', 'projects.', 'tasks.', 'dashboard.'] });
  const c = q.data?.connection;
  return (
    <div className="flex flex-col gap-4">
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title={t('admin.forma.connection')}>
              {!c ? (
                <Notice tone="warn">{t('admin.forma.notConfigured')}</Notice>
              ) : (
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                  <dt className="text-graphite">{t('admin.forma.mode')}</dt>
                  <dd>{t(`admin.forma.modes.${c.mode as 'mock'}`)}</dd>
                  <dt className="text-graphite">{t('admin.forma.hub')}</dt>
                  <dd>{c.accountLabel ?? c.hubId ?? '-'}</dd>
                  <dt className="text-graphite">{t('common.status')}</dt>
                  <dd>
                    <Pill tone={c.status === 'connected' ? 'good' : 'bad'}>{t(`admin.forma.connStatus.${c.status as 'connected'}`, { defaultValue: c.status })}</Pill>
                    {c.lastError && <div className="text-xs text-bad">{c.lastError}</div>}
                  </dd>
                  {c.refreshTokenExpiresAt && (
                    <>
                      <dt className="text-graphite">{t('admin.forma.token')}</dt>
                      <dd className="num">{dt(c.refreshTokenExpiresAt)}</dd>
                    </>
                  )}
                </dl>
              )}
              <div className="mt-3 text-sm">
                <span className="text-graphite">{t('admin.forma.nextRun')}: </span>
                <span className="num">{q.data.schedule.nextRunAt ? dt(q.data.schedule.nextRunAt) : '-'}</span>
                <span className="text-graphite">
                  {' '}
                  ({t('admin.settings.minutes', { count: q.data.schedule.intervalMinutes })}, {t(`admin.settings.windows.${q.data.schedule.window as 'daily'}`)})
                </span>
                <p className={clsx('mt-1 text-xs', q.data.schedule.runMinutes > q.data.schedule.intervalMinutes * 0.8 ? 'text-warn' : 'text-graphite')}>
                  {t('admin.forma.load', {
                    projects: q.data.schedule.activeProjects,
                    requests: q.data.schedule.requestsPerRun,
                    minutes: q.data.schedule.runMinutes,
                    perDay: num(q.data.schedule.requestsPerDay, 0),
                    skipped: q.data.schedule.skippedProjects,
                    rpm: q.data.schedule.requestsPerMinute,
                  })}
                  {q.data.schedule.runMinutes > q.data.schedule.intervalMinutes * 0.8 && ` ${t('admin.forma.loadWarn')}`}
                </p>
              </div>
              {operate && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {c?.mode === 'user_3lo' && (
                    <a href="/api/forma/connect">
                      <Button>{t('admin.forma.connect')}</Button>
                    </a>
                  )}
                  <Button icon={<RefreshCw className="size-4" />} loading={sync.isPending} onClick={() => sync.mutateAsync({}).catch(toast.error)}>
                    {t('admin.forma.syncAll')}
                  </Button>
                  <Button variant="ghost" loading={sync.isPending} onClick={() => sync.mutateAsync({ full: true }).catch(toast.error)}>
                    {t('admin.forma.fullAll')}
                  </Button>
                </div>
              )}
            </Panel>
            <Panel title={t('admin.forma.projects')} bodyClassName="p-0">
              <table className="w-full text-sm">
                <tbody>
                  {q.data.projects.map((p) => (
                    <tr key={p.id} className="border-b border-line/70">
                      <td className={td}>
                        <span className="num font-medium">{p.number}</span> {p.name}
                        <div className="text-xs text-graphite">{p.formaName}</div>
                      </td>
                      <td className={td}>
                        <Pill tone={p.formaState === 'active' ? 'good' : 'warn'}>{t(`projects.formaState.${p.formaState as 'active'}`)}</Pill>
                        {!p.syncEnabled && <Pill className="ml-1">{t('common.inactive')}</Pill>}
                      </td>
                      <td className={clsx(td, 'num text-xs text-graphite')}>{dt(p.lastSyncAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          </div>
          <Panel title={t('admin.forma.runs')} bodyClassName="p-0">
            <div className="scroll-x">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="border-b border-line bg-sheet-2">
                  <tr>
                    <th className={th}>{t('admin.audit.when')}</th>
                    <th className={th}>{t('common.project')}</th>
                    <th className={th}>{t('common.type')}</th>
                    <th className={th}>{t('common.status')}</th>
                    <th className={th}>{t('admin.forma.duration')}</th>
                    <th className={th}>{t('admin.forma.statsHeader')}</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.runs.map((r) => {
                    const s = r.stats as Record<string, number>;
                    return (
                      <tr key={r.id} className="border-b border-line/70">
                        <td className={clsx(td, 'num')}>{dt(r.startedAt)}</td>
                        <td className={td}>{r.projectNumber}</td>
                        <td className={td}>{t(`admin.forma.kind.${r.kind as 'full'}`)}</td>
                        <td className={td}>
                          <Pill tone={r.status === 'ok' ? 'good' : r.status === 'error' ? 'bad' : 'warn'}>{t(`admin.forma.runStatus.${r.status as 'ok'}`)}</Pill>
                          {r.error && <div className="max-w-md truncate text-xs text-bad" title={r.error}>{r.error}</div>}
                        </td>
                        <td className={clsx(td, 'num')}>{runDuration(r.startedAt, r.finishedAt)}</td>
                        <td className={clsx(td, 'num')}>
                          {s.fetched ?? 0} / {s.created ?? 0} / {s.updated ?? 0} / {(s.flaggedDeleted ?? 0) + (s.flaggedAccessLost ?? 0)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}

/** Duration of a sync run, e.g. "42 s" or "3:05 min"; "-" while it runs. */
function runDuration(startedAt: string, finishedAt: string | null): string {
  if (!finishedAt) return '-';
  const s = Math.max(0, Math.round((Date.parse(finishedAt) - Date.parse(startedAt)) / 1000));
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')} min`;
}

function BackupTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const { can } = useMe();
  const q = useQ('backup.snapshots', {});
  const create = useM('backup.createSnapshot', { invalidate: ['backup.'] });
  const [label, setLabel] = useState('');
  const [a, setA] = useState('');
  const [b, setB] = useState<string>('current');
  const [busy, setBusy] = useState<string | null>(null);
  const compare = useQ('backup.compare', { a, b }, { enabled: false });
  useEffect(() => {
    if (!a && q.data?.snapshots[0]) setA(q.data.snapshots[0].id);
  }, [q.data, a]);
  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    try {
      await fn();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };
  const cmp = compare.data;
  return (
    <div className="flex flex-col gap-4">
      <Notice tone="info">{t('admin.backup.hint')}</Notice>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title={t('admin.backup.exportAll')}>
          <div className="flex flex-wrap gap-2">
            <Button icon={<Download className="size-4" />} loading={busy === 'xlsx'} onClick={() => run('xlsx', async () => saveFile(await rpc('backup.exportAll', { format: 'xlsx' })))}>
              {t('admin.backup.exportXlsx')}
            </Button>
            <Button icon={<FileArchive className="size-4" />} loading={busy === 'json'} onClick={() => run('json', async () => saveFile(await rpc('backup.exportAll', { format: 'json' })))}>
              {t('admin.backup.exportJson')}
            </Button>
          </div>
        </Panel>
        <Panel title={t('admin.backup.powerbi')}>
          <p className="mb-3 text-sm text-graphite">{t('admin.backup.powerbiHint')}</p>
          <div className="flex flex-wrap gap-2">
            <Button icon={<Download className="size-4" />} loading={busy === 'bi'} onClick={() => run('bi', async () => saveFile((await rpc('backup.biExport', {})).file))}>
              {t('admin.backup.powerbiDownload')}
            </Button>
            <Button
              icon={<Upload className="size-4" />}
              loading={busy === 'biu'}
              onClick={() =>
                run('biu', async () => {
                  const r = await rpc('backup.biExport', { upload: true });
                  if (r.upload) (r.upload.uploaded ? toast.ok : toast.warn)(r.upload.message ?? '');
                })
              }
            >
              {t('admin.backup.powerbiUpload')}
            </Button>
          </div>
        </Panel>
      </div>
      <Panel
        title={t('admin.backup.snapshots')}
        bodyClassName="p-0"
        actions={
          can('backup.manage') && (
            <div className="flex flex-wrap gap-2">
              <Input className="h-8 w-48 max-w-full" placeholder={t('admin.backup.label')} value={label} onChange={(e) => setLabel(e.target.value)} />
              <Button size="sm" icon={<Camera className="size-3.5" />} loading={create.isPending} onClick={() => create.mutateAsync({ label: label || null }).then(() => setLabel('')).catch(toast.error)}>
                {t('admin.backup.create')}
              </Button>
            </div>
          )
        }
      >
        {q.isLoading && <Loading />}
        <div className="scroll-x">
          <table className="w-full min-w-[680px] text-sm">
            <thead className="border-b border-line bg-sheet-2">
              <tr>
                <th className={th}>{t('admin.audit.when')}</th>
                <th className={th}>{t('common.type')}</th>
                <th className={th}>{t('admin.backup.label')}</th>
                <th className={clsx(th, 'text-right')}>{t('admin.backup.size')}</th>
                <th className={clsx(th, 'text-right')}>{t('admin.backup.entriesCount')}</th>
                <th className={th} />
              </tr>
            </thead>
            <tbody>
              {(q.data?.snapshots ?? []).map((s) => (
                <tr key={s.id} className="border-b border-line/70">
                  <td className={clsx(td, 'num')}>{dt(s.createdAt)}</td>
                  <td className={td}>{t(`admin.backup.kinds.${s.kind as 'manual'}`)}</td>
                  <td className={td}>{s.label}</td>
                  <td className={clsx(td, 'num text-right')}>{s.sizeBytes ? `${(s.sizeBytes / 1024).toFixed(0)} KB` : '-'}</td>
                  <td className={clsx(td, 'num text-right')}>{s.counts.time_entry ?? '-'}</td>
                  <td className={clsx(td, 'text-right')}>
                    {can('data.export.all') && (
                      <Button size="sm" variant="ghost" loading={busy === s.id} onClick={() => run(s.id, async () => saveFile(await rpc('backup.download', { id: s.id })))}>
                        {t('admin.backup.download')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      <Panel title={t('admin.backup.compare')}>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="A">
            <Select value={a} onChange={(e) => setA(e.target.value)} className="w-64">
              {(q.data?.snapshots ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {dt(s.createdAt)} {s.label ?? ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('admin.backup.compareWith')}>
            <Select value={b} onChange={(e) => setB(e.target.value)} className="w-64">
              <option value="current">{t('admin.backup.current')}</option>
              {(q.data?.snapshots ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {dt(s.createdAt)} {s.label ?? ''}
                </option>
              ))}
            </Select>
          </Field>
          <Button disabled={!a} loading={compare.isFetching} onClick={() => compare.refetch()}>
            {t('admin.backup.compare')}
          </Button>
        </div>
        <ErrorBox error={compare.error} />
        {cmp && (
          <div className="mt-4 flex flex-col gap-4">
            <Notice tone={cmp.protectedRowsRemoved ? 'bad' : 'good'}>
              {cmp.protectedRowsRemoved ? t('admin.backup.protectedBad', { count: cmp.protectedRowsRemoved }) : t('admin.backup.protectedOk')}
            </Notice>
            <div className="scroll-x">
              <table className="w-full min-w-[560px] text-sm">
                <thead className="border-b border-line">
                  <tr>
                    <th className={th}>{t('admin.backup.table')}</th>
                    <th className={clsx(th, 'text-right')}>{t('admin.backup.before')}</th>
                    <th className={clsx(th, 'text-right')}>{t('admin.backup.after')}</th>
                    <th className={clsx(th, 'text-right')}>{t('admin.backup.added')}</th>
                    <th className={clsx(th, 'text-right')}>{t('admin.backup.removed')}</th>
                    <th className={clsx(th, 'text-right')}>{t('admin.backup.changed')}</th>
                  </tr>
                </thead>
                <tbody>
                  {cmp.tables
                    .filter((x) => x.added || x.removed || x.changed)
                    .map((x) => (
                      <tr key={x.table} className="border-b border-line/70">
                        <td className={td}>
                          {x.table} {x.protected && <Pill>{t('admin.backup.protected')}</Pill>}
                        </td>
                        <td className={clsx(td, 'num text-right')}>{x.before}</td>
                        <td className={clsx(td, 'num text-right')}>{x.after}</td>
                        <td className={clsx(td, 'num text-right')}>{x.added}</td>
                        <td className={clsx(td, 'num text-right', x.removed && x.protected && 'font-semibold text-bad')}>{x.removed}</td>
                        <td className={clsx(td, 'num text-right')}>{x.changed}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            {!cmp.detail && <p className="text-xs text-graphite">{t('admin.backup.countsOnly')}</p>}
            {cmp.entries.sample.length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-semibold">{t('admin.backup.entryChanges')}</h3>
                <ul className="flex max-h-72 flex-col gap-1 overflow-y-auto text-xs">
                  {cmp.entries.sample.slice(0, 200).map((c) => (
                    <li key={c.id + c.type} className="num border-b border-line/60 py-1">
                      <Pill tone={c.type === 'removed' ? 'bad' : c.type === 'added' ? 'good' : 'info'}>{t(`admin.backup.${c.type}`)}</Pill> {d(c.workDate)} {c.person}{' '}
                      <span className="text-graphite">
                        {Object.entries(c.fields)
                          .map(([k, [x, y]]) => `${k}: ${x ?? '-'} → ${y ?? '-'}`)
                          .join('; ')}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {cmp.months.length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-semibold">{t('admin.backup.monthTotals')}</h3>
                <ul className="num flex flex-col gap-1 text-sm">
                  {cmp.months.map((m) => (
                    <li key={m.month}>
                      {month(`${m.month}-01`)}: {h(m.before)} → {h(m.after)} h
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Panel>
    </div>
  );
}

const AUDIT_PAGE = 200;

function AuditTab() {
  const { t } = useTranslation();
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  // the log grows without end: pages of 200 events, further pages while scrolling
  const filters = { action: action || undefined, from: from || undefined };
  const q = useInfiniteQuery({
    queryKey: ['audit.listPaged', filters],
    queryFn: ({ pageParam }) => rpc('audit.list', { ...filters, limit: AUDIT_PAGE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, all) => (last.events.length >= AUDIT_PAGE ? all.length * AUDIT_PAGE : undefined),
    placeholderData: keepPreviousData,
  });
  const events = useMemo(() => q.data?.pages.flatMap((pg) => pg.events) ?? [], [q.data]);
  const loadMore = () => {
    if (q.hasNextPage && !q.isFetchingNextPage) void q.fetchNextPage();
  };
  const fmt = (v: unknown) => (v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v));
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-line bg-sheet p-3">
        <Field label={t('admin.audit.action')}>
          <Input value={action} onChange={(e) => setAction(e.target.value)} placeholder="time. / billing. / project." className="w-56" />
        </Field>
        <Field label={t('common.from')}>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
      </div>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && events.length === 0 && <EmptyState>{t('common.noResults')}</EmptyState>}
      {events.length > 0 && (
        <ScrollTable label={t('admin.tabs.audit')}>
          <table className="w-full min-w-[900px] text-[13px]">
            <thead className="border-b border-line bg-sheet-2">
              <tr>
                <th className={th}>{t('admin.audit.when')}</th>
                <th className={th}>{t('admin.audit.who')}</th>
                <th className={th}>{t('admin.audit.action')}</th>
                <th className={th}>{t('admin.audit.entity')}</th>
                <th className={th}>{t('admin.audit.change')}</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} className="border-b border-line/70 align-top">
                  <td className={clsx(td, 'num whitespace-nowrap')}>{dt(e.at)}</td>
                  <td className={td}>{e.actorName ?? 'System'}</td>
                  <td className={td}>
                    <code className="text-xs">{e.action}</code>
                    {e.projectNumber && <div className="text-xs text-graphite">{e.projectNumber}</div>}
                  </td>
                  <td className={clsx(td, 'text-xs text-graphite')}>
                    {e.entity}
                    <div className="max-w-[10rem] truncate" title={e.entityId ?? ''}>
                      {e.entityId}
                    </div>
                  </td>
                  <td className={clsx(td, 'max-w-xl text-xs')}>
                    {e.masked && <div className="text-faint italic">{t('admin.audit.masked')}</div>}
                    {e.partlyMasked && <div className="text-faint italic">{t('admin.audit.partlyMasked')}</div>}
                    {e.reason && <div className="font-medium">{e.reason}</div>}
                    {(e.before !== null || e.after !== null) && (
                      <div className="break-all text-graphite">
                        {fmt(e.before)} {e.after !== null && `→ ${fmt(e.after)}`}
                      </div>
                    )}
                    {e.meta !== null && <div className="break-all text-faint">{fmt(e.meta)}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {q.hasNextPage && <Sentinel onMore={loadMore} />}
        </ScrollTable>
      )}
      {events.length > 0 && (
        <p className="text-xs text-graphite">{q.isFetchingNextPage ? t('app.loading') : q.hasNextPage ? t('admin.audit.moreWhileScrolling', { count: events.length }) : t('admin.audit.shown', { count: events.length })}</p>
      )}
    </div>
  );
}

/** Outlook calendars: connection, last run, mailboxes with errors, manual run. */
function OutlookTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const q = useQ('outlook.status', {});
  const run = useM('outlook.syncNow', { invalidate: ['outlook.'] });
  const r = q.data?.lastRun?.result;
  return (
    <div className="flex flex-col gap-4">
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel title={t('admin.outlook.title')}>
            {!q.data.configured && <Notice tone="warn">{t('admin.outlook.notConfigured')}</Notice>}
            {q.data.configured && !q.data.settings.enabled && <Notice tone="neutral">{t('admin.outlook.off')}</Notice>}
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-graphite">{t('admin.outlook.connection')}</dt>
              <dd>{q.data.clientLabel ?? '-'}</dd>
              <dt className="text-graphite">{t('admin.outlook.targets')}</dt>
              <dd>
                {[q.data.settings.personalCalendars ? t('admin.outlook.personal') : null, q.data.settings.teamMailbox || null].filter(Boolean).join(', ') || '-'}
              </dd>
              <dt className="text-graphite">{t('admin.outlook.appointments')}</dt>
              <dd className="num">
                {q.data.appointments}
                {q.data.failing > 0 && <span className="text-bad"> ({t('admin.outlook.failing', { count: q.data.failing })})</span>}
              </dd>
              <dt className="text-graphite">{t('admin.outlook.lastRun')}</dt>
              <dd className="num">{q.data.lastRun ? dt(q.data.lastRun.at) : '-'}</dd>
              {r && (
                <>
                  <dt className="text-graphite">{t('admin.outlook.result')}</dt>
                  <dd>{t('admin.outlook.resultText', { created: r.created, updated: r.updated, removed: r.removed, errors: r.errors })}</dd>
                </>
              )}
            </dl>
            {q.data.canRun && (
              <Button
                className="mt-4"
                icon={<RefreshCw className="size-4" />}
                disabled={!q.data.configured || !q.data.settings.enabled}
                loading={run.isPending}
                onClick={() =>
                  run
                    .mutateAsync({})
                    .then((x) => toast.ok(t('admin.outlook.resultText', { created: x.created, updated: x.updated, removed: x.removed, errors: x.errors })))
                    .catch(toast.error)
                }
              >
                {t('admin.outlook.syncNow')}
              </Button>
            )}
            <p className="mt-3 text-xs text-graphite">{t('admin.outlook.hint')}</p>
          </Panel>
          <Panel title={t('admin.outlook.problems')}>
            {q.data.problems.length === 0 && <p className="text-sm text-graphite">{t('admin.outlook.noProblems')}</p>}
            <ul className="flex flex-col gap-2 text-sm">
              {q.data.problems.map((p) => (
                <li key={p.mailbox + p.error} className="border-b border-line/60 pb-2">
                  <div className="font-medium">
                    {p.mailbox} <span className="num text-graphite">({p.count})</span>
                  </div>
                  <div className="text-xs break-all text-bad">{p.error}</div>
                </li>
              ))}
            </ul>
          </Panel>
        </div>
      )}
    </div>
  );
}
